import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { NgxMaskDirective } from 'ngx-mask';
import { PhoneFormatPipe } from 'src/app/shared/pipes/phone-format.pipe';
import { ApiError } from '../../../abstract/api-error';
import { SponsorsService } from '../../../core/data-services/sponsors.service';
import { Sponsor } from '../../../core/data-models/sponsor.model';
import { SponsorFilters } from '../../../core/request-models/sponsor-filters.model';
import { SponsorCreateRequest } from '../../../core/request-models/sponsor-create-request.model';
import { environment } from '../../../../environments/environment';

/**
 * Sponsors List Component
 *
 * Displays paginated list of ADN Sponsors with search,
 * brand color swatches, and navigation to detail pages.
 *
 * Reference: /agent-os/specs/2026-02-11-acres-advantage/spec.md
 */
@Component({
  selector: 'app-sponsors-list',
  standalone: true,
  imports: [CommonModule, FormsModule, NgxMaskDirective, PhoneFormatPipe],
  templateUrl: './sponsors-list.component.html',
  styleUrl: './sponsors-list.component.scss'
})
export class SponsorsListComponent implements OnInit {
  sponsors: Sponsor[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalSponsors = 0;
  totalPages = 0;

  // Search
  searchQuery = '';

  // Add sponsor form
  showAddForm = false;
  addingSponsors = false;
  addError = '';
  newSponsor: SponsorCreateRequest = {
    name: '',
    website: null,
    phone: null,
    email: null,
  };

  constructor(
    private router: Router,
    private sponsorsService: SponsorsService
  ) {}

  ngOnInit(): void {
    this.loadSponsors();
  }

  /**
   * Load sponsors with current filters
   */
  loadSponsors(): void {
    this.loading = true;
    this.errorMessage = '';

    const filters: SponsorFilters = {
      page: this.currentPage,
      limit: this.pageSize
    };

    if (this.searchQuery.trim()) {
      filters.query = this.searchQuery.trim();
    }

    this.sponsorsService.listSponsors(filters).subscribe({
      next: (page) => {
        this.sponsors = page.items;
        this.totalSponsors = page.paging.totalItems;
        this.totalPages = page.paging.totalPages;
        this.loading = false;
      },
      error: (error) => {
        console.error('Failed to load sponsors:', error);
        this.errorMessage = 'Failed to load sponsors';
        this.loading = false;
      }
    });
  }

  /**
   * Handle search
   */
  onSearch(): void {
    this.currentPage = 1;
    this.loadSponsors();
  }

  /**
   * Clear search
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadSponsors();
  }

  /**
   * Navigate to page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.loadSponsors();
    }
  }

  /**
   * Get pagination range
   */
  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    const end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Navigate to sponsor detail
   */
  viewSponsor(sponsor: Sponsor): void {
    this.router.navigate(['/sponsors', sponsor.id]);
  }

  /**
   * Resolve a logo path to a full CDN URL.
   * Handles both relative paths and legacy full URLs.
   */
  getLogoFullUrl(logoUrl: string | null | undefined): string {
    if (!logoUrl) return '';
    if (logoUrl.startsWith('http')) return logoUrl;
    return `${environment.appCDN}/${logoUrl}`;
  }

  // ============================================
  // ADD SPONSOR
  // ============================================

  toggleAddForm(): void {
    this.showAddForm = !this.showAddForm;
    this.addError = '';
    if (this.showAddForm) {
      this.newSponsor = { name: '', website: null, phone: null, email: null };
    }
  }

  submitNewSponsor(): void {
    this.addError = '';

    if (!this.newSponsor.name?.trim()) {
      this.addError = 'Sponsor name is required';
      return;
    }

    this.addingSponsors = true;

    this.sponsorsService.createSponsor({
      ...this.newSponsor,
      name: this.newSponsor.name.trim(),
    }).subscribe({
      next: (created) => {
        this.addingSponsors = false;
        this.showAddForm = false;
        if (created) {
          this.router.navigate(['/sponsors', created.id]);
        } else {
          this.loadSponsors();
        }
      },
      error: (err) => {
        console.error('Failed to create sponsor:', err);
        this.addError = ApiError.fromHttpError(err).message;
        this.addingSponsors = false;
      }
    });
  }

  /**
   * Archive a sponsor
   */
  archiveSponsor(sponsor: Sponsor, event: Event): void {
    event.stopPropagation();

    if (!confirm(`Are you sure you want to archive "${sponsor.name}"?`)) {
      return;
    }

    this.sponsorsService.archiveSponsor(sponsor.id).subscribe({
      next: () => {
        this.loadSponsors();
      },
      error: (err) => {
        console.error('Failed to archive sponsor:', err);
        this.errorMessage = 'Failed to archive sponsor';
      }
    });
  }

  readonly Math = Math;
}
